/**
 * Silent usage tracker — writes to `platformUsage` Firestore collection.
 * Companies have NO read access to this collection (Firestore rules).
 * This file contains zero UI elements and is never imported in company-facing pages.
 */
import { db } from '../firebase/config';
import { doc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';

// Internal key — short and unmemorable on purpose
const _SID = '_ps';

/**
 * Called once when a user successfully logs in and their profile resolves.
 */
export const saStartSession = async (uid, userName, userEmail, userRole, companyId) => {
  if (!uid || !companyId) return;
  const sid = `${companyId}__${uid}__${Date.now()}`;
  try {
    sessionStorage.setItem(_SID, sid);
    await setDoc(doc(db, 'platformUsage', sid), {
      companyId,
      userId:    uid,
      userName:  userName  || '',
      userEmail: userEmail || '',
      userRole:  userRole  || 'counsellor',
      loginAt:     serverTimestamp(),
      logoutAt:    null,
      lastSeenAt:  serverTimestamp(),
      pages: [],
      active: true,
      // Basic device hint — nothing sensitive
      _ua: (navigator.userAgent || '').substring(0, 100),
    });
  } catch { /* silent — tracking must never break the app */ }
};

/**
 * Called on explicit logout and on beforeunload.
 */
export const saEndSession = async () => {
  const sid = sessionStorage.getItem(_SID);
  if (!sid) return;
  sessionStorage.removeItem(_SID);
  try {
    await updateDoc(doc(db, 'platformUsage', sid), {
      logoutAt:   serverTimestamp(),
      lastSeenAt: serverTimestamp(),
      active:     false,
    });
  } catch { /* silent */ }
};

/**
 * Called every time the user navigates to a different page inside the CRM.
 */
export const saTrackPage = async (page) => {
  const sid = sessionStorage.getItem(_SID);
  if (!sid || !page) return;
  try {
    await updateDoc(doc(db, 'platformUsage', sid), {
      lastSeenAt: serverTimestamp(),
      pages: arrayUnion({ p: page, t: Date.now() }),
    });
  } catch { /* silent */ }
};
